/**
 * Last-good cache for read screens (spec section 5: the garage, jobs and
 * build sheets are readable offline). A fresh load is saved to the local
 * store; if the network is unreachable the cached copy is served instead.
 * Real API errors (not found, signed out) are never hidden by the cache.
 */
import { ApiRequestError, NetworkError } from '@/api/http'
import { localStore } from '@/native/storage'

export interface Cached<T> {
  value: T
  /** True when this came from the device because the network was unreachable. */
  offline: boolean
}

export async function cachedRead<T>(key: string, fetcher: () => Promise<T>): Promise<Cached<T>> {
  try {
    const value = await fetcher()
    await localStore()
      .set(`cache:${key}`, value)
      .catch(() => {})
    return { value, offline: false }
  } catch (err) {
    // No signal, or the server is unreachable behind a proxy / gateway.
    const unreachable =
      err instanceof NetworkError ||
      (err instanceof ApiRequestError && [502, 503, 504].includes(err.status))
    if (!unreachable) throw err
    const cached = await localStore()
      .get<T>(`cache:${key}`)
      .catch(() => undefined)
    if (cached === undefined) throw err
    return { value: cached, offline: true }
  }
}

/** Forget cached reads, e.g. on sign-out so the next rider never sees them. */
export async function clearCache(): Promise<void> {
  const store = localStore()
  const keys = await store.keys('cache:').catch(() => [] as string[])
  await Promise.all(keys.map((k) => store.delete(k).catch(() => {})))
}
